import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { LandPlot, Loader2 } from 'lucide-react';

import { AssetCard } from '../components/assets/AssetCard';
import { assetService } from '../services/assetService';
import { knowledgeService } from '../services/knowledgeService';
import type { Asset } from '../types/domain';
import { getParticleTitle } from '../utils/particleDisplay';

export function KnowledgePasListPage() {
  const { particleId = '' } = useParams();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [particleTitle, setParticleTitle] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!particleId) return;
    setLoading(true);
    setError(null);
    try {
      const [nextAssets, particle] = await Promise.all([
        assetService.listPasAssetsByParticleId(particleId),
        knowledgeService.getKnowledgeParticle(particleId).catch(() => null),
      ]);
      setAssets(Array.isArray(nextAssets) ? nextAssets : []);
      setParticleTitle(particle ? getParticleTitle(particle) : particleId);
    } catch (loadError) {
      setAssets([]);
      setError(loadError instanceof Error ? loadError.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [particleId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <Link
            to={`/knowledge/${encodeURIComponent(particleId)}`}
            className="text-sm text-primary"
          >
            ← 返回颗粒
          </Link>
          <h1 className="text-2xl font-semibold text-ink">实战答题纸</h1>
          {particleTitle ? (
            <p className="truncate text-sm text-muted" title={particleTitle}>{particleTitle}</p>
          ) : null}
          <p className="text-sm text-muted">共 {assets.length} 张</p>
        </div>
      </div>

      {error ? (
        <div className="rounded-xl border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger">
          {error}
          <button type="button" className="ml-3 underline" onClick={() => { void load(); }}>
            重试
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="animate-spin text-primary" />
        </div>
      ) : assets.length === 0 && !error ? (
        <div className="rounded-xl border border-border bg-surface px-5 py-8 text-center shadow-card">
          <LandPlot className="mx-auto mb-3 text-muted" size={28} />
          <p className="font-medium text-ink">暂无实战答题纸</p>
          <p className="mt-1 text-sm text-muted">
            还没有与该知识颗粒绑定的实战答题纸。可在 Mobile 端扫描绑定，或从资源菜单上传。
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {assets.map((asset) => (
            <AssetCard
              key={asset.id}
              asset={asset}
              to={`/assets/${encodeURIComponent(asset.id)}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
